import React, { useEffect } from "react";
import { Link, Outlet, useLoaderData, useLocation } from "react-router-dom";

import { alertActions } from "../../../features/alert/alertSlice";
import { globalActions } from "../../../features/global/globalSlice";
import { useAppDispatch } from "../../../hooks/reduxHooks";
import { getOrganizations } from "../../../lib/api";
import { IAPIResponse, IOrganization } from "../../../types/api";

export const loader = async () => {
    return {
        organizations: await getOrganizations(),
        header: "Organizations",
    };
};

const OrganizationIndex = (): React.JSX.Element | undefined => {
    const dispatch = useAppDispatch();
    const location = useLocation();

    const {
        organizations: { organizations },
    } = useLoaderData() as {
        organizations: IAPIResponse & { organizations: IOrganization[] };
    };

    organizations.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
    });

    const isOnOrgPage = /organizations\/?$/.test(location.pathname);

    useEffect(() => {
        const pendingApproval = organizations.filter(o => o.approved === 0);
        if (pendingApproval.length > 0 && isOnOrgPage) {
            dispatch(
                alertActions.warning({
                    title: "Pending Organization Changes",
                    text: (
                        <React.Fragment>
                            <p className="mb-0">
                                The following organizations have pending changes
                                to review:
                            </p>
                            <ul className="text-left list-unstyled mb-0">
                                {pendingApproval.map(o => (
                                    <li>
                                        <Link
                                            to={`league-management/organizations/edit/${o.id}`}
                                            className="text-decoration-none text-primary"
                                        >
                                            {o.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </React.Fragment>
                    ),
                }),
            );
        }
    }, [organizations, location]);

    return isOnOrgPage ? (
        <div className="organizationsContent">
            <h2>Organizational Users</h2>
            <Link
                to={`/league-management/organizations/add`}
                className="btn btn-primary createButton"
            >
                Add New Organization
            </Link>
            {organizations.map(o => (
                <section className="organization" key={o.id}>
                    <div className="orgLeft">
                        <h4>
                            <Link to={`edit/${o.id}`}>{o.name}</Link>
                        </h4>
                        <div className="orgUserInfo">
                            <b>{o.primaryUser.name}</b>
                            <br />
                            {o.primaryUser.email}
                            <br />
                            {o.primaryUser.phoneNumbers.primary} (primary)
                            <br />
                            {o.primaryUser.phoneNumbers.secondary &&
                                `${o.primaryUser.phoneNumbers.secondary} (secondary)<br />`}
                            {o.primaryUser.phoneNumbers.other &&
                                `${o.primaryUser.phoneNumbers.other} (other) <br />`}
                        </div>
                    </div>
                    <div className="orgRight">
                        <div className="majorTeams">
                            <b>MAJOR Teams</b>
                            <ul>
                                {o.teams.majorTeams.map(t => (
                                    <li key={`major-${t.id}`}>
                                        {t.name} ({t.ageLevel})
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="WITeams">
                            <b>WI Teams</b>
                            <ul>
                                {o.teams.WITeams.map(t => (
                                    <li key={`wibb-${t.id}`}>
                                        {t.name} ({t.ageLevel})
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="ILTeams">
                            <b>IL Teams</b>
                            <ul>
                                {o.teams.ILTeams.map(t => (
                                    <li key={`ilbb-${t.id}`}>
                                        {t.name} ({t.ageLevel})
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            ))}
        </div>
    ) : (
        <Outlet />
    );
};

export default OrganizationIndex;
